import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Pagination from '../../pagination/Pagination/Pagination';
import ShowMoreBtn from '../components/showMoreBtn/ShowMoreBtn/ShowMoreBtn';
import GridTable from '../components/GridTable/GridTable';
import { useWidgetTranslates } from '../../../common/utils/utils';
import css from './GridComponent.module.scss';
import classnames from 'classnames';
import { generateCompStylers, generateStylers } from '../../../common/utils/styles';
import { useWindowDimensions } from '../../../common/utils/windowDimensions';
import FixedLastColumnGridComponent from './FixedLastColumnGridComponent/FixedLastColumnGridComponent';
import { useFixedColumnHeight } from '../components/helpers';

const GridComponent = ({
    gridName,
    setGridData,
    gridStyles = {},
    setViewData,
    viewData,
    viewThead,
    cellWidthArray,
    cellThAddButton,
    convertData,
    handlers = {},
    variant = 2,
    openSwitcher,
    setUrl,
    pageSettings = {},
    sticky = false,
    fullScreen = false,
    showThead = true,
    component,
    addBtn,
    token,
    emptyDataText,
    maxGridHeight = false,
    filter
}) => {
    const windowDimensions = useWindowDimensions();
    const isMobile = windowDimensions < 768;
    const [tableData, setTableData] = useState(viewData);
    const paginationLinks = tableData?.links;
    const paginationMeta = tableData?.meta;
    const refTable = useRef(null);
    const refTableChild = useRef(null);

    const [isOverflow, setIsOverflow] = useState(false);

    const fixedLastColumnShow = useMemo(
        () => isOverflow && sticky?.rightStickyColumnName,
        [isOverflow, sticky?.rightStickyColumnName]
    );

    useEffect(() => {
        setTableData(viewData);
    }, [viewData]);
    const transl = useWidgetTranslates('grid');
    useFixedColumnHeight(sticky?.rightStickyColumnName, gridName, windowDimensions, viewData);

    const tableProps = {
        gridName,
        setGridData,
        cellThAddButton,
        viewThead,
        viewData,
        cellWidthArray,
        refTable,
        tableData,
        openSwitcher,
        handlers,
        variant,
        gridStyles,
        showThead,
        fullScreen,
        refTableChild,
        emptyDataText,
        translation: transl,
        pageSettings,
        fixedLastColumnShow,
        maxGridHeight
    };

    const gridWrapper = generateStylers(gridStyles?.gridWrapper);
    const { stylers, classes } = generateCompStylers(gridStyles?.tableWrapper);

    const fixedGridProps = {
        gridStyles,
        gridName,
        gridWrapper,
        sticky,
        fullScreen,
        classes,
        stylers,
        variant,
        tableProps,
        viewData,
        tableData,
        viewThead,
        isMobile,
        fixedLastColumnShow,
        isFixedLastColumnShow: fixedLastColumnShow,
        maxGridHeight
    };

    if (!tableData || !viewData) return null;
    return (
        <div
            id={`${gridName}portalDropdown`}
            style={gridWrapper.style}
            className={classnames({ 'sticky-table-fullscreen': sticky && fullScreen }, css.gridWrapper, gridWrapper.className)}
            ref={refTable}
        >
            <div
                className={classnames(
                    !fixedLastColumnShow || fullScreen ? css.gridWrapperOverflow : '',
                    gridStyles?.tableWrapper ? [css.gridWrapperBorderRadius, classes.wrapper] : [css.gridWrapperBorderRadius]
                )}
                ref={refTableChild}
                style={{ ...gridStyles?.tableWrapTable, ...(gridStyles?.tableWrapper ? { ...stylers?.wrapper } : {}) }}
            >
                {sticky?.rightStickyColumnName ? <FixedLastColumnGridComponent {...fixedGridProps} /> : null}
                {pageSettings?.view ? (
                    <pageSettings.view data={tableData} gridStyles={gridStyles} params={pageSettings?.params} />
                ) : (
                    <GridTable
                        sticky={sticky}
                        {...tableProps}
                        setIsOverflow={setIsOverflow}
                        viewThead={
                            fixedLastColumnShow
                                ? {
                                      ...viewThead,
                                      data: viewThead?.data.map((el) => {
                                          if (el.name === sticky?.rightStickyColumnName) {
                                              return {
                                                  ...el,
                                                  ...(isMobile ? { title: '' } : {})
                                              };
                                          }
                                          return el;
                                      })
                                  }
                                : viewThead
                        }
                        isFixedLastColumnShow={fixedLastColumnShow}
                    />
                )}
                {component ? component : null}
            </div>
            {!!paginationLinks && !!paginationMeta && paginationMeta.links.length > 3 && variant !== 3 && (
                <div className={classnames(css.PaginationWrap, classes.paginationWrapper)}>
                    <Pagination
                        smallPagination={pageSettings.smallPagination || false}
                        setViewData={setViewData}
                        links={paginationLinks}
                        meta={paginationMeta}
                        setTableData={setTableData}
                        thead={tableData?.thead}
                        convertData={convertData}
                        setUrl={setUrl}
                        gridStyles={gridStyles}
                        token={token}
                        filter={filter}
                    />
                </div>
            )}
            {variant == 3 && addBtn && (
                <ShowMoreBtn
                    setViewData={setViewData}
                    convertData={convertData}
                    setTableData={setViewData || setTableData}
                    paginationLinks={paginationLinks}
                    convertEndUrl={addBtn.convertEndUrl}
                    url={addBtn.url}
                    showMoreEndUrl={addBtn.showMoreEndUrl}
                    setShowMoreEndUrl={addBtn.setShowMoreEndUrl}
                    textClose={addBtn.textClose || ''}
                    textOpen={addBtn.textOpen || ''}
                    filterArr={addBtn.filterArr}
                    count={addBtn.count}
                    translation={transl}
                    token={token}
                />
            )}
        </div>
    );
};
GridComponent.propTypes = {
    gridName: PropTypes.string,
    setGridData: PropTypes.func,
    gridStyles: PropTypes.object,
    setViewData: PropTypes.func,
    viewData: PropTypes.object,
    viewThead: PropTypes.object,
    cellWidthArray: PropTypes.array,
    cellThAddButton: PropTypes.array,
    convertData: PropTypes.func,
    handlers: PropTypes.object,
    variant: PropTypes.oneOf(['1', '2', '3', 1, 2, 3]),
    openSwitcher: PropTypes.object,
    setUrl: PropTypes.func,
    pageSettings: PropTypes.object,
    sticky: PropTypes.bool,
    fullScreen: PropTypes.bool,
    showThead: PropTypes.bool,
    addBtn: PropTypes.object,
    token: PropTypes.string
};
export default GridComponent;
